import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { mainSitelinkCandidates, secondaryNavigation } from "../data/siteNavigation";
import { brandIdentity } from "../data/brand";

const primaryLabels = new Set([
  "Shop Humanoids",
  "Humanoid Robots",
  "Robotics News",
  "Physical AI",
  "Robot Companies",
  "Robotics Guides",
]);

const primaryNavigation = mainSitelinkCandidates.filter((item) => primaryLabels.has(item.label));
const editorialNavigation = mainSitelinkCandidates.filter((item) => !primaryLabels.has(item.label));
const roboticsResources = secondaryNavigation.filter((item) =>
  ["Data Hub", "Jobs Intelligence", "Humanoid Intelligence", "Robot Types", "Robot Sensors", "Robot Components", "Robotics Software", "Robotics Events"].includes(item.label),
);
const otherSections = secondaryNavigation.filter((item) => !roboticsResources.includes(item));

function DesktopGroup({ title, items }) {
  return (
    <div>
      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-neutral-500">{title}</p>
      <div className="mt-3 grid gap-1.5">
        {items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="focus-ring rounded-xl px-3 py-2 text-sm font-bold text-neutral-800 transition hover:bg-blue-50 hover:text-blue-700"
          >
            {item.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

function MobileGroup({ title, items, onNavigate }) {
  if (!items.length) return null;
  return (
    <div>
      <p className="mb-2 text-[11px] font-black uppercase tracking-[0.16em] text-neutral-500">{title}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className="focus-ring rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm font-bold text-neutral-800 transition hover:border-blue-200 hover:text-blue-700"
          >
            {item.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `focus-ring rounded-xl px-3 py-2 text-[13px] font-bold whitespace-nowrap transition ${
      isActive
        ? "bg-neutral-950 text-white"
        : "text-neutral-700 hover:bg-neutral-100 hover:text-neutral-950"
    }`;

  return (
    <header lang="en" className="sticky top-0 z-50 border-b border-neutral-200/80 bg-white/95 shadow-[0_8px_30px_rgba(15,23,42,0.05)] backdrop-blur-xl">
      <div className="site-shell flex min-h-16 items-center justify-between gap-4 py-2">
        <Link
          to="/"
          className="focus-ring group flex shrink-0 items-center gap-3 rounded-xl"
          onClick={() => setOpen(false)}
          aria-label="TechniaHQRobot homepage"
        >
          <img
            src={brandIdentity.logoPath}
            alt="TechniaHQRobot logo"
            width="40"
            height="40"
            className="h-10 w-10 rounded-xl shadow-[0_10px_28px_rgba(37,99,235,0.18)]"
          />
          <span className="leading-tight">
            <span className="block text-base font-black tracking-tight text-neutral-950">TechniaHQRobot</span>
            <span className="hidden text-[11px] font-bold text-neutral-500 sm:block">Humanoid Robots &amp; Physical AI</span>
          </span>
        </Link>

        <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
          <div className="hidden min-w-0 items-center justify-end gap-1 xl:flex">
            <nav className="flex items-center gap-0.5" aria-label="Main navigation">
            {primaryNavigation.map((item) => (
              <NavLink key={item.to} to={item.to} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <details className="group relative">
            <summary className="focus-ring flex cursor-pointer list-none items-center gap-1.5 rounded-xl px-3 py-2 text-[13px] font-bold text-neutral-700 transition hover:bg-neutral-100 hover:text-neutral-950 [&::-webkit-details-marker]:hidden">
              More
              <span aria-hidden="true" className="text-[10px] transition group-open:rotate-180">▼</span>
            </summary>
            <div className="absolute right-0 top-[calc(100%+0.65rem)] w-[720px] max-w-[calc(100vw-2rem)] rounded-2xl border border-neutral-200 bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.14)]">
              <div className="grid gap-6 md:grid-cols-3">
                <DesktopGroup title="Editorial" items={editorialNavigation} />
                <DesktopGroup title="Robotics resources" items={roboticsResources} />
                <DesktopGroup title="Other sections" items={otherSections} />
              </div>
            </div>
          </details>
          </div>


          <button
          type="button"
          className="focus-ring inline-flex min-h-11 items-center gap-2 rounded-xl border border-neutral-300 bg-white px-4 py-2 text-sm font-black text-neutral-950 shadow-sm xl:hidden"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls="mobile-menu"
        >
          <span className="grid gap-1" aria-hidden="true">
            <span className="block h-0.5 w-4 rounded-full bg-neutral-950" />
            <span className="block h-0.5 w-4 rounded-full bg-neutral-950" />
          </span>
          {open ? "Close" : "Menu"}
          </button>
        </div>
      </div>

      {open && (
        <div id="mobile-menu" className="max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-neutral-200 bg-white px-4 py-4 xl:hidden">
          <div className="mx-auto grid max-w-7xl gap-5">
            <nav className="grid gap-2 sm:grid-cols-2" aria-label="Mobile main navigation">
              {primaryNavigation.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `focus-ring min-h-11 rounded-xl px-4 py-3 text-sm font-black ${isActive ? "bg-neutral-950 text-white" : "border border-neutral-200 bg-neutral-50 text-neutral-900"}`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="grid gap-5 border-t border-neutral-200 pt-5">
              <MobileGroup title="Editorial" items={editorialNavigation} onNavigate={() => setOpen(false)} />
              <MobileGroup title="Robotics resources" items={roboticsResources} onNavigate={() => setOpen(false)} />
              <MobileGroup title="Other sections" items={otherSections} onNavigate={() => setOpen(false)} />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
